"use client";

import { useCallback, useEffect, useRef } from "react";
import { useDebug } from "@huub-web/ui";
import type { FormFieldSchema } from "@huub-web/ui";
import { isString } from "@huub-web/utils";

import { useTemplates } from "./useTemplates";

/**
 * Wizard step metadata for agent context reporting.
 */
export type WizardStepInfo = {
  /**
   * Step key matching the slide identifier
   */
  key: string;
  /**
   * Human-readable step title (from i18n)
   */
  title: string;
  /**
   * Step description/subtitle (from i18n)
   */
  description?: string;
  /**
   * Whether this step only appears under certain conditions (e.g. dependent on earlier answers).
   * Helps the agent know to skip steps that are not currently applicable.
   */
  conditional?: boolean;
};

type UseAgentFormAwarenessParams = {
  /**
   * Form identifier used as the inputUpdate prefix
   */
  formId: string;
  /**
   * Type of form (determines how backend processes the state)
   */
  formType: "form" | "filter" | "wizard";
  /**
   * Field schema from Form.getFieldSchema() — passed by the template.
   * Each field already contains its current value; no separate state dict needed.
   */
  fieldSchema: FormFieldSchema[];
  /**
   * Wizard-specific metadata (optional)
   */
  wizard?: {
    currentStep: string;
    currentStepIndex: number;
    steps: WizardStepInfo[];
    totalSteps: number;
  };
  /**
   * When true, the hook is a no-op — no registration or deregistration.
   * Used when a parent template defers to a child's registration.
   */
  skip?: boolean;
};

/**
 * A mapped field ready for the agent context payload.
 * Includes optional group metadata used for hierarchy grouping.
 */
type MappedField = {
  fieldKey: string;
  label: string;
  type?: string;
  inputType?: string;
  options?: string[];
  required?: boolean;
  description?: string;
  value: unknown;
  /** Repeating group key (e.g. "mortgage-7448") — used for grouping, stripped before send */
  _group?: string;
  /** Human-readable group name (e.g. "Hypotheek 1") */
  _groupName?: string;
  /** Parent group key for nested groups */
  _parentGroup?: string;
};

/**
 * A hierarchical group of fields within a panel (e.g. "Hypotheek 1").
 */
type FieldGroup = {
  key: string;
  title: string;
  fields: Omit<MappedField, "_group" | "_groupName" | "_parentGroup">[];
  children?: FieldGroup[];
};

/**
 * Groups flat mapped fields into a hierarchical structure based on
 * their _group/_parentGroup metadata from FormGroupContext.
 *
 * Returns { ungrouped, children } where ungrouped are fields without
 * group metadata and children is the nested group hierarchy.
 */
const buildGroupHierarchy = (
  fields: MappedField[],
): {
  ungrouped: Omit<MappedField, "_group" | "_groupName" | "_parentGroup">[];
  children: FieldGroup[];
} => {
  const ungrouped: Omit<
    MappedField,
    "_group" | "_groupName" | "_parentGroup"
  >[] = [];
  const topGroups = new Map<
    string,
    {
      title: string;
      fields: MappedField[];
      subGroups: Map<string, { title: string; fields: MappedField[] }>;
    }
  >();

  for (const field of fields) {
    if (!field._group) {
      const { _group, _groupName, _parentGroup, ...clean } = field;
      ungrouped.push(clean);
      continue;
    }

    if (field._parentGroup) {
      // Nested group (e.g. leningdeel inside hypotheek)
      // Ensure parent group exists
      if (!topGroups.has(field._parentGroup)) {
        topGroups.set(field._parentGroup, {
          title: field._parentGroup,
          fields: [],
          subGroups: new Map(),
        });
      }
      const parent = topGroups.get(field._parentGroup);
      if (!parent) continue;
      if (!parent.subGroups.has(field._group)) {
        parent.subGroups.set(field._group, {
          title: field._groupName ?? field._group,
          fields: [],
        });
      }
      parent.subGroups.get(field._group)?.fields.push(field);
    } else {
      // Top-level group (e.g. hypotheek)
      if (!topGroups.has(field._group)) {
        topGroups.set(field._group, {
          title: field._groupName ?? field._group,
          fields: [],
          subGroups: new Map(),
        });
      }
      const group = topGroups.get(field._group);
      if (!group) continue;
      // Update title if we have a better name
      if (field._groupName) {
        group.title = field._groupName;
      }
      group.fields.push(field);
    }
  }

  const stripMeta = (
    f: MappedField,
  ): Omit<MappedField, "_group" | "_groupName" | "_parentGroup"> => {
    const { _group, _groupName, _parentGroup, ...clean } = f;
    return clean;
  };

  const children: FieldGroup[] = Array.from(topGroups.entries()).map(
    ([key, group]) => ({
      key,
      title: group.title,
      fields: group.fields.map(stripMeta),
      ...(group.subGroups.size > 0
        ? {
            children: Array.from(group.subGroups.entries()).map(
              ([subKey, sub]) => ({
                key: subKey,
                title: sub.title,
                fields: sub.fields.map(stripMeta),
              }),
            ),
          }
        : {}),
    }),
  );

  return { ungrouped, children };
};

/**
 * Reports form field schema and state to the AI agent via the unified
 * UI context registry. Replaces per-template static schema definitions
 * with dynamic, form-driven metadata.
 *
 * Field values are included directly in each field's schema entry
 * (from getFieldSchema()). No separate state dict is needed.
 *
 * For wizard forms with nested ModelChildren (e.g. Hypotheken > Leningdelen),
 * fields tagged with group metadata are automatically grouped into a
 * hierarchical `children` structure within each panel.
 *
 * Debouncing and WebSocket reconnect handling are done centrally by
 * TemplatesProvider — this hook only registers/deregisters its slice.
 */
export const useAgentFormAwareness = ({
  formId,
  formType,
  fieldSchema,
  wizard,
  skip,
}: UseAgentFormAwarenessParams) => {
  const { registerUIContext, deregisterUIContext } = useTemplates();
  const { log } = useDebug();
  const lastPayloadJsonRef = useRef<string>("");

  const mapField = useCallback(
    (f: FormFieldSchema): MappedField => ({
      fieldKey: f.fieldKey ?? f.name,
      label: isString(f.label) ? f.label : "",
      type: f.type,
      ...(f.inputType ? { inputType: f.inputType } : {}),
      ...(f.options ? { options: f.options.map((o) => o.value) } : {}),
      ...(f.required ? { required: true } : {}),
      ...(f.description ? { description: f.description } : {}),
      value: f.value ?? null,
      ...(f.group ? { _group: f.group } : {}),
      ...(f.groupName ? { _groupName: f.groupName } : {}),
      ...(f.parentGroup ? { _parentGroup: f.parentGroup } : {}),
    }),
    [],
  );

  useEffect(() => {
    if (skip) return;

    if (fieldSchema.length === 0) {
      lastPayloadJsonRef.current = "";
      deregisterUIContext?.(formId);
      return;
    }

    let formContextBody: Record<string, unknown>;
    if (wizard) {
      const fieldsByStep = new Map<string, MappedField[]>();
      for (const f of fieldSchema) {
        const step = f.step ?? "";
        if (!fieldsByStep.has(step)) fieldsByStep.set(step, []);
        fieldsByStep.get(step)?.push(mapField(f));
      }

      formContextBody = {
        formId,
        currentStep: wizard.currentStep,
        panels: wizard.steps.map((s) => {
          const panelFields = fieldsByStep.get(s.key) ?? [];
          const { ungrouped, children } = buildGroupHierarchy(panelFields);
          return {
            key: s.key,
            title: s.title,
            ...(s.description ? { description: s.description } : {}),
            ...(s.conditional ? { conditional: true } : {}),
            current: s.key === wizard.currentStep,
            fields: ungrouped,
            ...(children.length > 0 ? { children } : {}),
          };
        }),
      };
    } else {
      const mapped = fieldSchema.map(mapField);
      const hasGroups = mapped.some((f) => f._group);
      if (hasGroups) {
        const { ungrouped, children } = buildGroupHierarchy(mapped);
        formContextBody = {
          formId,
          fields: ungrouped,
          ...(children.length > 0 ? { children } : {}),
        };
      } else {
        formContextBody = {
          formId,
          fields: mapped.map(({ _group, _groupName, _parentGroup, ...f }) => f),
        };
      }
    }

    const slicePayload = {
      type: "form" as const,
      formType,
      formContext: formContextBody,
    };
    const json = JSON.stringify(slicePayload);

    if (json === lastPayloadJsonRef.current) {
      return;
    }
    lastPayloadJsonRef.current = json;

    log("brown", "useAgentFormAwareness", `register (${formType})`, {
      id: formId,
      fields: fieldSchema.length,
      ...(wizard ? { step: wizard.currentStep } : {}),
    });

    registerUIContext?.(formId, slicePayload);
  }, [
    skip,
    fieldSchema,
    wizard,
    formId,
    formType,
    registerUIContext,
    deregisterUIContext,
    mapField,
    log,
  ]);

  useEffect(() => {
    if (skip) return;
    return () => {
      lastPayloadJsonRef.current = "";
      deregisterUIContext?.(formId);
    };
  }, [formId, deregisterUIContext, skip]);
};
